/**
 * TrackingOverlay Component
 * ~~~***Real-Time Bounding Box Visualization***~~~
 * 
 * Purpose: Overlays tracking boxes on video that sync with playback
 * 
 * Features:
 * - Real-time sync with video currentTime
 * - Shows all verified targets with their tracking boxes
 * - Color-coded by verification type (PRIMARY/SECONDARY/UNVERIFIED)
 * - Smooth transitions as boxes move
 * - Toggle on/off with button
 * 
 * Court Value: Visual proof of what the AI tracked and when
 */

import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/v4-tracking-overlay.css';

const TrackingOverlay = ({
  targets = [],
  videoElement = null,
  isEnabled = false,
  onClose = null
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [activeBoxes, setActiveBoxes] = useState([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Parse timestamp string to seconds
  const parseTimestamp = useCallback((timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === 'number') return timestamp;
    
    const parts = timestamp.split(':');
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseFloat(seconds);
    } else if (parts.length === 2) {
      const [minutes, seconds] = parts;
      return parseInt(minutes) * 60 + parseFloat(seconds);
    }
    return parseFloat(timestamp) || 0;
  }, []);

  // Convert coords array [left, top, right, bottom] to box format
  const coordsToBox = useCallback((coords) => {
    if (!coords || coords.length < 4) return null;
    
    // coords format: [left, top, right, bottom] - all normalized 0-1
    const [left, top, right, bottom] = coords;
    
    const width = Math.max(0, Math.min(1, right - left));
    const height = Math.max(0, Math.min(1, bottom - top));
    
    // Flag if box is suspiciously large (covers more than 50% of screen in either dimension)
    const isOversized = width > 0.5 || height > 0.6;
    
    return {
      left: Math.max(0, Math.min(1, left)),
      top: Math.max(0, Math.min(1, top)),
      width,
      height,
      isOversized // Flag for UI to show warning
    };
  }, []);

  // Find the closest VEHICLE bounding box for a target at current time
  const findVehicleBoxAtTime = useCallback((target, time) => {
    if (!target.bounding_boxes || target.bounding_boxes.length === 0) {
      return null;
    }

    // Check if current time is within target's visibility range
    const firstSeen = parseTimestamp(target.first_seen);
    const lastSeen = parseTimestamp(target.last_seen);
    
    if (firstSeen === null || lastSeen === null) return null;
    if (time < firstSeen - 0.5 || time > lastSeen + 0.5) return null;

    // Find closest box to current time
    let closestBox = null;
    let closestDiff = Infinity;

    target.bounding_boxes.forEach(box => {
      const boxTime = parseTimestamp(box.time);
      if (boxTime === null) return;
      
      const diff = Math.abs(boxTime - time);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestBox = box;
      }
    });

    // Only show if within 0.5 seconds of a tracked frame
    if (closestDiff > 0.5) return null;

    return closestBox;
  }, [parseTimestamp]);

  // Find the closest PLATE bounding box for a target at current time (Session 7)
  const findPlateBoxAtTime = useCallback((target, time) => {
    // Check all appearance segments for plate detections
    const segments = target.appearance_segments || [];
    
    let closestPlate = null;
    let closestDiff = Infinity;

    segments.forEach(segment => {
      const plateDetections = segment.plate_detections || [];
      
      plateDetections.forEach(pd => {
        const plateTime = pd.time_seconds;
        if (plateTime === null || plateTime === undefined) return;
        
        const diff = Math.abs(plateTime - time);
        if (diff < closestDiff && diff < 0.15) { // Tighter tolerance for plate (150ms)
          closestDiff = diff;
          closestPlate = {
            time: plateTime,
            coords: pd.bounding_box,
            confidence: pd.confidence
          };
        }
      });
    });

    return closestPlate;
  }, []);

  // Get color based on anchor type
  const getBoxColor = (target) => {
    if (target.anchor_type === 'PRIMARY') return '#22c55e'; // Green
    if (target.anchor_type === 'SECONDARY') return '#3b82f6'; // Blue
    return '#f59e0b'; // Orange for unverified
  };

  // Get label for target
  const getTargetLabel = (target) => {
    const attrs = target.attributes || {};
    
    if (target.target_type === 'vehicle') {
      const color = attrs.color || '';
      const make = attrs.make || '';
      const model = attrs.model || '';
      const plate = attrs.license_plate && attrs.license_plate !== 'Not available' 
        ? ` [${attrs.license_plate}]` : '';
      return `${color} ${make} ${model}${plate}`.trim() || 'Vehicle';
    }
    
    return target.target_type || 'Target';
  };

  // Get verification badge
  const getVerificationBadge = (target) => {
    if (target.anchor_type === 'PRIMARY') return '🔐';
    if (target.anchor_type === 'SECONDARY') return '📍';
    return '⚠️';
  };

  // Update container size when video element changes
  useEffect(() => {
    if (!videoElement) return;

    const updateSize = () => {
      setContainerSize({
        width: videoElement.clientWidth || videoElement.offsetWidth,
        height: videoElement.clientHeight || videoElement.offsetHeight
      });
    };

    updateSize();
    
    // Also update on video metadata loaded
    videoElement.addEventListener('loadedmetadata', updateSize);
    window.addEventListener('resize', updateSize);

    return () => {
      videoElement.removeEventListener('loadedmetadata', updateSize);
      window.removeEventListener('resize', updateSize);
    };
  }, [videoElement]);

  // Listen to video time updates
  useEffect(() => {
    if (!videoElement || !isEnabled) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };

    // Update more frequently for smoother tracking
    const interval = setInterval(() => {
      if (videoElement && !videoElement.paused) {
        setCurrentTime(videoElement.currentTime);
      }
    }, 50); // 20fps update

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('seeked', handleTimeUpdate);

    return () => {
      clearInterval(interval);
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('seeked', handleTimeUpdate);
    };
  }, [videoElement, isEnabled]);

  // Calculate active boxes whenever time or targets change
  // Option C: Show BOTH vehicle boxes (blue) and plate boxes (green) when available
  useEffect(() => {
    if (!isEnabled || targets.length === 0) {
      setActiveBoxes([]);
      return;
    }

    const boxes = [];

    targets.forEach(target => {
      // Only show verified targets
      if (!target.vi_verified && target.anchor_type === 'NONE') return;
      
      // 1. VEHICLE BOX (TRACKED tier - blue)
      const vehicleBoxData = findVehicleBoxAtTime(target, currentTime);
      if (vehicleBoxData) {
        const vehicleCoords = coordsToBox(vehicleBoxData.coords);
        if (vehicleCoords) {
          boxes.push({
            id: `${target.target_id}-vehicle`,
            ...vehicleCoords,
            label: getTargetLabel(target),
            badge: '○', // TRACKED indicator
            color: '#3b82f6', // Blue for vehicle/tracked
            confidence: target.confidence || 0.9,
            anchorType: 'TRACKED',
            boxType: 'vehicle',
            isOversized: vehicleCoords.isOversized
          });
        }
      }
      
      // 2. PLATE BOX (VERIFIED tier - green) - Only when plate detected at this timestamp
      const plateBoxData = findPlateBoxAtTime(target, currentTime);
      if (plateBoxData && plateBoxData.coords) {
        const plateCoords = coordsToBox(plateBoxData.coords);
        if (plateCoords) {
          boxes.push({
            id: `${target.target_id}-plate`,
            ...plateCoords,
            label: target.attributes?.license_plate || 'PLATE',
            badge: '✓', // VERIFIED indicator
            color: '#22c55e', // Green for plate/verified
            confidence: plateBoxData.confidence || 0.95,
            anchorType: 'VERIFIED',
            boxType: 'plate',
            isOversized: false // Plates shouldn't be oversized
          });
        }
      }
    });

    setActiveBoxes(boxes);
  }, [currentTime, targets, isEnabled, findVehicleBoxAtTime, findPlateBoxAtTime, coordsToBox]);

  // Don't render if disabled or no video
  if (!isEnabled || !videoElement) return null;

  return (
    <div className="tracking-overlay-container">
      {/* Header with info and close button */}
      <div className="tracking-header">
        <div className="tracking-info">
          <span className="tracking-icon">📦</span>
          <span className="tracking-label">VI TRACKING</span>
          <span className="tracking-count">{activeBoxes.length} active</span>
        </div>
        {onClose && (
          <button className="tracking-close-btn" onClick={onClose} title="Hide tracking">
            ✕
          </button>
        )}
      </div>

      {/* Legend - Session 7 Hybrid Model */}
      <div className="tracking-legend">
        <span className="legend-item" style={{ color: '#22c55e' }}>✓ VERIFIED (Plate)</span>
        <span className="legend-item" style={{ color: '#3b82f6' }}>○ TRACKED (Vehicle)</span>
      </div>

      {/* Bounding boxes - Vehicle (blue) and Plate (green) */}
      {activeBoxes.map(box => (
        <div
          key={box.id}
          className={`tracking-box ${box.boxType || 'vehicle'} ${box.anchorType?.toLowerCase() || 'unknown'} ${box.isOversized ? 'oversized' : ''}`}
          style={{
            left: `${box.left * 100}%`,
            top: `${box.top * 100}%`,
            width: `${box.width * 100}%`,
            height: `${box.height * 100}%`,
            borderColor: box.isOversized ? '#f59e0b' : box.color,
            borderWidth: box.boxType === 'plate' ? '3px' : '2px',
            borderStyle: box.boxType === 'plate' ? 'solid' : 'dashed',
            boxShadow: box.isOversized 
              ? `0 0 0 2px rgba(245, 158, 11, 0.3)` 
              : box.boxType === 'plate'
                ? `0 0 0 3px ${box.color}50, 0 0 20px ${box.color}80, inset 0 0 10px ${box.color}30`
                : `0 0 0 2px ${box.color}30, 0 0 10px ${box.color}40`,
            opacity: box.isOversized ? 0.5 : 1,
            zIndex: box.boxType === 'plate' ? 20 : 10 // Plate boxes on top
          }}
        >
          {/* Label - Different style for plate vs vehicle */}
          <div 
            className={`tracking-box-label ${box.boxType === 'plate' ? 'plate-label' : 'vehicle-label'}`}
            style={{ 
              backgroundColor: box.isOversized ? '#f59e0b' : box.color,
              fontSize: box.boxType === 'plate' ? '11px' : '10px',
              fontWeight: box.boxType === 'plate' ? '700' : '600',
              padding: box.boxType === 'plate' ? '3px 8px' : '2px 6px'
            }}
          >
            <span className="box-badge">{box.isOversized ? '⚠️' : box.badge}</span>
            <span className="box-label-text">
              {box.boxType === 'plate' ? box.label : box.label}
            </span>
            {box.isOversized ? (
              <span className="box-warning">LOOSE FIT</span>
            ) : (
              <span className="box-confidence">{Math.round(box.confidence * 100)}%</span>
            )}
          </div>

          {/* Corner markers - Only for vehicle boxes */}
          {box.boxType !== 'plate' && (
            <>
              <div className="tracking-corner tl" style={{ borderColor: box.isOversized ? '#f59e0b' : box.color }} />
              <div className="tracking-corner tr" style={{ borderColor: box.isOversized ? '#f59e0b' : box.color }} />
              <div className="tracking-corner bl" style={{ borderColor: box.isOversized ? '#f59e0b' : box.color }} />
              <div className="tracking-corner br" style={{ borderColor: box.isOversized ? '#f59e0b' : box.color }} />
            </>
          )}
        </div>
      ))}

      {/* Time indicator */}
      <div className="tracking-time">
        {formatTime(currentTime)}
      </div>
    </div>
  );
};

// Format seconds to HH:MM:SS.mmm
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

export default TrackingOverlay;

